import {
    Box,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Print, Search } from "@material-ui/icons"
import React, { useCallback, useEffect, useState } from "react"
import type { ErroApi, ImpressaoDoDia } from "../../../services/impressoesDia"
import { obterImpressoesDoDia } from "../../../services/impressoesDia"
import { useSnackbar } from "../../../utils/notificacoes"

// Rotulos amigaveis para o campo `evento` (Constantes do backend). PRINT1..PRINTn sao
// as vias por setor/estacao da impressao alternada.
const LABEL_EVENTO: Record<string, string> = {
    PLACED: "Novo pedido",
    PRINT: "Comanda",
    CUPOM_FISCAL: "Cupom fiscal",
    CANCELLED: "Cancelamento",
}

function rotuloEvento(evento: string | null): string {
    if (!evento) return "—"
    if (LABEL_EVENTO[evento]) return LABEL_EVENTO[evento]
    // PRINT1, PRINT2, ... -> "Comanda (setor N)"
    const alternada = /^PRINT(\d+)$/.exec(evento)
    if (alternada) return `Comanda (setor ${alternada[1]})`
    return evento
}

const LABEL_TIPO_PEDIDO: Record<number, string> = {
    0: "Delivery",
    1: "Retirada",
    2: "Mesa",
    3: "Kiosque",
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1000px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
    },
    tipoChip: {
        backgroundColor: "#e3f2fd",
        color: "#1565c0",
        fontWeight: 600,
    },
    eventoChip: {
        backgroundColor: "#ede7f6",
        color: "#5e35b1",
        fontWeight: 600,
    },
    contador: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(1),
    },
}))

// Data de hoje no formato YYYY-MM-DD, no fuso local (evita o -1 dia do toISOString UTC).
function hojeLocalISO(): string {
    const agora = new Date()
    const ano = agora.getFullYear()
    const mes = String(agora.getMonth() + 1).padStart(2, "0")
    const dia = String(agora.getDate()).padStart(2, "0")
    return `${ano}-${mes}-${dia}`
}

function formatarHora(valor: string | number | null): string {
    if (valor == null) return "—"
    const data = new Date(valor)
    if (isNaN(data.getTime())) return String(valor)
    return data.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    })
}

export function TabelaImpressoesDoDia() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const autorizacao = localStorage.getItem("chaveSistema") || ""

    const [data, setData] = useState<string>(hojeLocalISO())
    const [itens, setItens] = useState<ImpressaoDoDia[]>([])
    const [carregando, setCarregando] = useState<boolean>(false)

    const carregar = useCallback(
        async (dia: string) => {
            if (!autorizacao) {
                enqueueSnackbar("Sessão expirada. Faça login novamente.", { variant: "error" })
                return
            }
            setCarregando(true)
            try {
                const resposta = await obterImpressoesDoDia(autorizacao, dia)
                if ("retornoErro" in resposta) {
                    enqueueSnackbar(
                        (resposta as ErroApi).mensagem ||
                            "Não foi possível carregar as impressões.",
                        { variant: "error" }
                    )
                    return
                }
                setItens(resposta)
            } catch (error) {
                enqueueSnackbar("Erro ao carregar as impressões.", { variant: "error" })
            } finally {
                setCarregando(false)
            }
        },
        [autorizacao, enqueueSnackbar]
    )

    // Carrega o dia de hoje ao abrir a tela.
    useEffect(() => {
        carregar(hojeLocalISO())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleBuscar = () => {
        if (!data) {
            enqueueSnackbar("Selecione um dia.", { variant: "warning" })
            return
        }
        carregar(data)
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={3}>
                    <Typography variant="h5" className={classes.titleText}>
                        Impressões do Dia
                    </Typography>
                    <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                        Todas as impressões enviadas para a loja no dia selecionado (comandas,
                        cupons fiscais e demais eventos), impressas ou não.
                    </Typography>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Print className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Impressões enviadas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filterRow}>
                            <TextField
                                label="Dia"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={data}
                                onChange={e => setData(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 180 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Search />}
                                onClick={handleBuscar}
                                disabled={carregando}
                                style={{ borderRadius: 25 }}
                            >
                                Buscar
                            </Button>
                        </Box>

                        {itens.length === 0 ? (
                            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                {carregando
                                    ? "Carregando…"
                                    : "Nenhuma impressão enviada nesse dia."}
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body2" className={classes.contador}>
                                    {itens.length}{" "}
                                    {itens.length === 1 ? "impressão" : "impressões"}
                                </Typography>
                                <TableContainer style={{ overflowX: "auto" }}>
                                    <Table
                                        size="small"
                                        stickyHeader
                                        style={{ width: "100%", minWidth: 620 }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ minWidth: 170 }}>
                                                    Hora do envio
                                                </TableCell>
                                                <TableCell style={{ minWidth: 110 }}>
                                                    Pedido
                                                </TableCell>
                                                <TableCell style={{ minWidth: 170 }}>
                                                    Tipo de impressão
                                                </TableCell>
                                                <TableCell style={{ minWidth: 110 }}>
                                                    Origem
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {itens.map(imp => (
                                                <TableRow key={imp.id} hover>
                                                    <TableCell>
                                                        {formatarHora(imp.dataCadastro)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {imp.pedidoId != null
                                                            ? `#${imp.pedidoId}`
                                                            : "—"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={rotuloEvento(imp.evento)}
                                                            size="small"
                                                            className={classes.eventoChip}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {imp.tipoPedido != null &&
                                                        LABEL_TIPO_PEDIDO[imp.tipoPedido] ? (
                                                            <Chip
                                                                label={
                                                                    LABEL_TIPO_PEDIDO[imp.tipoPedido]
                                                                }
                                                                size="small"
                                                                className={classes.tipoChip}
                                                            />
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
